/* eslint-disable import/no-cycle */
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import useProminentNavigation from "../../../ExperimentGate/useProminentNavigation";
import LeftSide from "./LeftSide/LeftSide";
import RightSide from "./RightSide/RightSide";
import * as elementsActions from "../../../../redux/actions/elements";
import styles from "./NavigationBar.module.css";
import "./NavigationBarBrandRefresh.module.css";
import {
  getNavHomepageProps,
  getNavPartProps,
  getNavRegionProps,
  isNavigationBarBrandRefresh,
} from "./navigationBarBrandRefreshClasses";
import { getCartLineQuantity } from "../../../../shared/utilities/orders";

const NavigationBar = ({
  userToken,
  userCart,
  hideHeaderMenuOnScroll,
  toggleMobileMenu,
  userOrderMethod,
  selectedStore,
  userAddress,
  toggleHeaderWidget,
  openHeaderWidgetFromOrderNow,
  isMenuPage,
  showNotification,
  amountAdded,
  productAddedImagePath,
  isHomepage,
  useFullscreenCarousel,
  hasActiveOrders,
  showCartMenu,
}) => {
  const prominentNavigation = useProminentNavigation();
  const cartQuantity = getCartLineQuantity(userCart);
  const isLoggedIn = !!userToken;
  const isOnHomepage = isHomepage && useFullscreenCarousel;
  const isBrandRefresh = isNavigationBarBrandRefresh();

  // Legacy path — identical className/markup when flag is off
  if (!isBrandRefresh) {
    return (
      <nav
        className={`
        ${styles.navContainer}
        ${isMenuPage && !hideHeaderMenuOnScroll && !showNotification ? "hidden" : ""}
        ${isOnHomepage ? styles.onHomepage : styles.notOnHomepage}
        w-full h-20 md:h-[68px] py-3
      `}
        role="navigation"
        aria-label="Main navigation"
        style={useFullscreenCarousel ? { touchAction: 'none' } : undefined}
      >
        <div
          className={styles.navContentContainer}
          data-cart-menu-open={showCartMenu ? "true" : undefined}
        >
          <LeftSide
            isLoggedIn={isLoggedIn}
            userOrderMethod={userOrderMethod}
            selectedStore={selectedStore}
            userAddress={userAddress}
            onToggleHeaderWidget={toggleHeaderWidget}
            hasActiveOrders={hasActiveOrders}
          />

          <RightSide
            isLoggedIn={isLoggedIn}
            cartQuantity={cartQuantity}
            userOrderMethod={userOrderMethod}
            selectedStore={selectedStore}
            userAddress={userAddress}
            onToggleHeaderWidget={toggleHeaderWidget}
            onOpenHeaderWidgetFromOrderNow={openHeaderWidgetFromOrderNow}
            onToggleMobileMenu={toggleMobileMenu}
            showNotification={showNotification}
            amountAdded={amountAdded}
            productAddedImagePath={productAddedImagePath}
            isMenuPage={isMenuPage}
          />
        </div>
      </nav>
    );
  }

  // Brand refresh — layout via :global(.brand-refresh) + data-* (Figma web)
  return (
    <nav
      className={`
        ${styles.navContainer}
        ${isMenuPage && !prominentNavigation && !hideHeaderMenuOnScroll && !showNotification ? "hidden" : ""}
        ${isOnHomepage ? styles.onHomepage : styles.notOnHomepage}
      `}
      role="navigation"
      aria-label="Main navigation"
      style={useFullscreenCarousel ? { touchAction: 'none' } : undefined}
      data-prominent-navigation={prominentNavigation ? "true" : undefined}
      data-region={getNavRegionProps()}
      data-part={getNavPartProps("nav-container")}
      data-homepage={getNavHomepageProps(isOnHomepage)}
    >
      <div
        className={styles.navContentContainer}
        data-part={getNavPartProps("nav-content-container")}
        data-cart-menu-open={showCartMenu ? "true" : undefined}
      >
        <LeftSide
          isLoggedIn={isLoggedIn}
          userOrderMethod={userOrderMethod}
          selectedStore={selectedStore}
          userAddress={userAddress}
          onToggleHeaderWidget={toggleHeaderWidget}
          hasActiveOrders={hasActiveOrders}
        />

        <RightSide
          isLoggedIn={isLoggedIn}
          cartQuantity={cartQuantity}
          userOrderMethod={userOrderMethod}
          selectedStore={selectedStore}
          userAddress={userAddress}
          onToggleHeaderWidget={toggleHeaderWidget}
          onOpenHeaderWidgetFromOrderNow={openHeaderWidgetFromOrderNow}
          onToggleMobileMenu={toggleMobileMenu}
          showNotification={showNotification}
          amountAdded={amountAdded}
          productAddedImagePath={productAddedImagePath}
          isMenuPage={isMenuPage}
        />
      </div>
    </nav>
  );
};

NavigationBar.propTypes = {
  userToken: PropTypes.string,
  userCart: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.number,
    }),
  ),
  hideHeaderMenuOnScroll: PropTypes.bool,
  toggleMobileMenu: PropTypes.func.isRequired,
  toggleHeaderWidget: PropTypes.func.isRequired,
  openHeaderWidgetFromOrderNow: PropTypes.func.isRequired,
  userOrderMethod: PropTypes.string,
  selectedStore: PropTypes.shape({
    name: PropTypes.string,
  }),
  userAddress: PropTypes.shape({
    address1: PropTypes.string,
    savedLocation: PropTypes.shape({
      locationLabel: PropTypes.shape({
        label: PropTypes.string,
      }),
    }),
  }),
  userInfo: PropTypes.shape({}),
  isMenuPage: PropTypes.bool,
  showNotification: PropTypes.bool,
  amountAdded: PropTypes.number,
  productAddedImagePath: PropTypes.string,
  isHomepage: PropTypes.bool,
  useFullscreenCarousel: PropTypes.bool,
  hasActiveOrders: PropTypes.bool,
  showCartMenu: PropTypes.bool,
};

NavigationBar.defaultProps = {
  userToken: null,
  userCart: [],
  hideHeaderMenuOnScroll: true,
  userOrderMethod: "",
  selectedStore: null,
  userAddress: null,
  userInfo: {},
  isMenuPage: false,
  showNotification: false,
  amountAdded: 0,
  productAddedImagePath: null,
  isHomepage: false,
  useFullscreenCarousel: false,
  hasActiveOrders: false,
  showCartMenu: false,
};

export const mapStateToProps = (state) => ({
  userToken: state.user.userToken,
  userCart: state.user?.userCart,
  userCartId: state.user.userCartId,
  hideHeaderMenuOnScroll: state.elements.hideHeaderMenuOnScroll,
  userOrderMethod: state.user.userOrderMethod,
  selectedStore: state.user.selectedStore,
  userAddress: state.user.userAddress,
  userInfo: state.user.userInfo,
  isStickyCategories: state.elements.isStickyCategories,
  hasActiveOrders: state.elements?.hasActiveOrders || false,
  showCartMenu: state.elements?.showCartMenu ?? false,
});

export const mapDispatchToProps = (dispatch) => ({
  toggleMobileMenu: () => dispatch(elementsActions.toggleMobileMenu()),
  toggleHeaderWidget: () => dispatch(elementsActions.toggleHeaderWidget()),
  openHeaderWidgetFromOrderNow: () =>
    dispatch(elementsActions.openHeaderWidgetFromOrderNow()),
});

export default connect(mapStateToProps, mapDispatchToProps)(NavigationBar);
